import React, { useState } from "react";
import styled, { css } from "styled-components";
import { NavLink, Link, useLocation, useNavigate } from "react-router-dom";
import { motion } from 'framer-motion';
import { BsArrowLeft } from 'react-icons/bs';
import TextShift from "../../utils/TextShift";

// Title container on the left
const NavTitle = styled.div`
  display: flex;
  align-items: center;

  @media (max-width: 768px) {
    margin-top: 0.5rem;
  }
`;

const Nav = styled.nav`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 2rem 0 0 0;
  gap: 4rem;

  @media (max-width: 768px) {
    padding: 1.5rem 0 4rem 0;
    margin-bottom: 1rem;
    align-items: flex-start;
  }
`;

const NavWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;

  overflow: hidden;
`;

const Hamburger = styled.div`
  display: none;
  flex-direction: column;
  justify-content: space-around;
  width: 30px;
  position: absolute;
  height: 25px;
  top: 32px;
  right: 20px;
  z-index: 11;

  @media (max-width: 768px) {
    display: flex;
  }

  div {
    background-color: white;
    height: 3px;
    transition: all 0.3s linear;
  }

  &.open div:nth-child(1) {
    transform: translateY(11px) rotate(45deg);
  }

  &.open div:nth-child(2) {
    opacity: 0;
  }

  &.open div:nth-child(3) {
    transform: translateY(-6px) rotate(-45deg);
  }
`;

const NavLinks = styled.ul`
  list-style: none;
  display: flex;
  align-items: center;
  transition: all 0.5s ease;
  gap: 2rem;
  margin: 0;
  padding: 0;

  li {
    margin: 0 0.5rem;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    position: absolute;
    top:  10px;
    right: 25px;
    padding: 1rem 0;
    opacity: 0;
    visibility: hidden;
    z-index: 10;
    border-radius: 0 0 10px 10px;
    gap: 1rem;

    li {
      margin: 0rem 0;
      position: relative;
    }

    li:nth-child(1) {
      padding-left: 0;
    }

    li:nth-child(2) {
      padding-left: 3rem;
    }

    li:nth-child(3) {
      padding-left: 6rem;
    }
  }

  &.open {
    opacity: 1;
    visibility: visible;
  }
`;

const StyledNavLink = styled(NavLink)`
  text-decoration: none;
  color: white;
  font-family: "Asap", sans-serif;
  transition: 0.3s ease-in-out;
  font-weight: 200;

  &:hover {
    font-weight: 900;
  }

  &.active {
    font-weight: 700;
  }
`;

const MenuCircle = styled.div`
  display: none;
  position: absolute;
  top: 0;
  right: 1px;
  rotate: 270deg;
  background-color: rgba(40, 40, 40, 0.97);
  border-radius: 100% 0 0 0;
  transition: width 0.5s ease, height 0.5s ease;
  overflow: hidden;
  transform: translateZ(0);
  z-index: 1;

  @media (max-width: 768px) {
    display: block;
    width: 0;
    height: 0;
  }

  &.open {
    width: 160px;
    height: 160px;
    transition-timing-function: cubic-bezier(0.68, -0.55, 0.27, 1.55);
  }
`;

const BackButton = styled(motion.button)`
  background: none;
  border: none;
  color: #8C8C8C;
  padding: 0.5rem;
  display: flex;
  align-items: center;
  font-size: 1.5rem;
  position: fixed;
  left: 1.5rem;
  top: 50%;
  transition: color 0.3s ease;
  z-index: 1000;

  &:hover {
    color: white;
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

function Navigation({ title }) {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const showBackButton = location.pathname !== '/' && window.innerWidth > 768;

  const handleBack = () => {
    const pathParts = location.pathname.split('/');
    if (pathParts[1] === 'projects') {
      navigate('/');
    } else if (pathParts[1] === 'experiments') {
      navigate('/');
    } else if (pathParts[1] === 'work') {
      navigate('/');
    } else if (pathParts[1] === 'play') {
      navigate('/');
    } else {
      navigate(-1);
    }
  };

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  return (
    <NavWrapper>
      <Nav>
        {showBackButton && (
          <BackButton
            onClick={handleBack}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
          >
            <BsArrowLeft />
          </BackButton>
        )}
        {title && (
          <NavTitle>
            <TextShift
              text={title}
              maxWeight={900}
              minWeight={100}
              fontSize="2rem"
              mobileFontSize="2rem"
              adjacentWeightChange={200}
              fontFamily="moret-variable"
            />
          </NavTitle>
        )}

        {/* Hamburger remains absolute so it can float at the top-right */}
        <Hamburger onClick={toggleMenu} className={isOpen ? "open" : ""}>
          <div></div>
          <div></div>
          <div></div>
        </Hamburger>

        <MenuCircle className={isOpen ? "open" : ""} />

        <NavLinks className={isOpen ? "open" : ""}>
          <li>
            <StyledNavLink
              to="/"
              end
              onClick={() => setIsOpen(false)}
            >
              Home
            </StyledNavLink>
          </li>
          <li>
            <StyledNavLink
              to="/about"
              onClick={() => setIsOpen(false)}
            >
              About
            </StyledNavLink>
          </li>
          <li>
            <StyledNavLink
              to="/resume"
              onClick={() => setIsOpen(false)}
            >
              Resume
            </StyledNavLink>
          </li>
        </NavLinks>
      </Nav>
    </NavWrapper>
  );
}

export default Navigation;
